<template>
  <div
    class="h-full rounded-xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-gray-900/60 hover:shadow md:transition-shadow"
    :class="[cardClass, { 'md:col-span-2': wide }]"
    aria-label="Runner card"
  >
    <div class="w-full p-5">
      <div class="flex flex-col gap-4">
        <h3
          class="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white break-words"
          :title="nameToShow"
        >
          <HighlightText :text="nameToShow" :query="searchQuery ?? ''" />
          <span
            v-if="burstOf !== null"
            class="inline-flex items-center rounded-md bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700 inset-ring inset-ring-violet-600/20 dark:bg-violet-400/10 dark:text-violet-300 dark:inset-ring-violet-400/30"
            :title="BURST_HINT"
            data-testid="burst-badge"
          >
            Burst
          </span>
        </h3>
        <p
          v-if="burstOf !== null"
          class="-mt-3 text-xs font-medium text-gray-500 dark:text-gray-400"
          data-testid="burst-anchor"
        >
          Added by the autoscaling of {{ burstOf || 'a deleted runner' }}
        </p>
        <p
          v-if="burstRunningText"
          class="-mt-3 text-xs text-gray-500 tabular-nums dark:text-gray-400"
          :title="burstRunningText.title"
          data-testid="burst-running-time"
        >
          {{ burstRunningText.text }}
        </p>
        <p
          v-if="platformShown"
          class="-mt-3 flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400"
          data-testid="runner-platform"
        >
          {{ PLATFORM_LABELS[platform] }}
        </p>
        <div v-if="!readOnly" class="flex flex-wrap items-center justify-end gap-2 sm:gap-4">
          <!-- Wrapper keeps hover/focus tooltips available even when the button is disabled -->
          <span
            v-if="burstOf === null"
            class="inline-flex rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            :title="duplicateButtonTitle"
            :tabindex="isDuplicateDisabled ? 0 : undefined"
          >
            <button
              type="button"
              class="disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:border-white/10 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              :disabled="isDuplicateDisabled"
              :aria-label="duplicateAriaLabel"
              :aria-disabled="isDuplicateDisabled || undefined"
              @click="onDuplicate"
            >
              <DocumentDuplicateIcon class="size-5" aria-hidden="true" />
              <span class="hidden sm:inline">Duplicate</span>
            </button>
          </span>

          <span
            v-if="burstOf === null"
            class="inline-flex rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            :title="pauseResumeButtonTitle"
            :tabindex="isPauseResumeDisabled ? 0 : undefined"
          >
            <button
              type="button"
              class="disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2"
              :class="pauseResumeButtonClass"
              :disabled="isPauseResumeDisabled"
              :aria-label="pauseResumeAriaLabel"
              :aria-disabled="isPauseResumeDisabled || undefined"
              @click="onTogglePause"
            >
              <PlayIcon v-if="isPausedLocal" class="size-5" aria-hidden="true" />
              <PauseIcon v-else class="size-5" aria-hidden="true" />
              <ExclamationTriangleIcon
                v-if="showPauseCapacityWarning"
                class="size-4"
                aria-hidden="true"
              />
              <span class="hidden sm:inline">{{ pauseResumeLabel }}</span>
            </button>
          </span>

          <button
            v-if="needsFix && burstOf === null"
            type="button"
            :disabled="planRequired"
            class="disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 shadow-sm hover:bg-red-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
            aria-label="Fix runner token"
            :title="planRequired ? PLAN_REQUIRED_HINT : 'Fix Token'"
            @click="openFixModal"
          >
            <ExclamationTriangleIcon class="size-5" aria-hidden="true" />
            <span class="hidden sm:inline">Fix</span>
          </button>

          <button
            v-else-if="burstOf === null"
            type="button"
            :disabled="planRequired"
            class="disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:border-white/10 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
            :aria-label="'Edit runner'"
            :title="planRequired ? PLAN_REQUIRED_HINT : 'Edit'"
            @click="emit('edit')"
          >
            <PencilSquareIcon class="size-5" aria-hidden="true" />
            <span class="hidden sm:inline">Edit</span>
          </button>

          <button
            v-if="showDelete"
            :disabled="isDeleteButtonDisabled"
            ref="deleteBtn"
            type="button"
            @click="onDeleteClick"
            :aria-label="deleteArmed ? 'Confirm delete runner' : 'Delete runner'"
            :title="deleteArmed ? 'Click again to confirm delete' : 'Delete'"
            :class="deleteButtonClass"
          >
            <TrashIcon class="size-5" aria-hidden="true" />
            <span class="ml-1 hidden sm:inline">Delete</span>
          </button>
        </div>
      </div>

      <!-- Error message banner -->
      <div
        v-if="errorMessage"
        class="mt-4 flex items-start gap-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
      >
        <ExclamationTriangleIcon class="size-5 shrink-0 mt-0.5" aria-hidden="true" />
        <p class="flex-1 break-words">{{ errorMessage }}</p>
        <button
          type="button"
          class="shrink-0 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
          @click="emit('dismiss-error')"
          aria-label="Dismiss error"
          title="Dismiss"
        >
          <svg class="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <slot name="schedule" />

      <div class="my-4 border-t border-gray-200 dark:border-white/10" />

      <div :class="{ 'xl:grid xl:grid-cols-2 xl:items-start xl:gap-x-6': wide }" data-testid="runner-body">
        <dl class="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-2">
          <div class="space-y-1">
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Status</dt>
            <dd class="text-sm font-medium">
              <span
                class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                :class="statusBadgeClass"
              >
                <span v-if="isPulsing" class="relative inline-flex w-2 h-2" aria-hidden="true">
                  <span
                    class="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping [animation-duration:1.75s]"
                    :class="statusDotClass"
                  ></span>
                  <span
                    class="relative inline-flex w-2 h-2 rounded-full"
                    :class="statusDotClass"
                  ></span>
                </span>
                <span
                  v-else
                  class="inline-block size-2 rounded-full"
                  :class="statusDotClass"
                  aria-hidden="true"
                />
                {{ statusLabel }}
              </span>
            </dd>
          </div>
          <div class="space-y-1">
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Chip</dt>
            <dd class="text-sm font-medium text-gray-900 dark:text-white">
              <HighlightText :text="String(specsToShow.chip)" :query="searchQuery ?? ''" />
            </dd>
          </div>
          <div class="space-y-1">
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">vCPU</dt>
            <dd class="text-sm font-medium text-gray-900 dark:text-white">
              <HighlightText :text="String(specsToShow.vcpu)" :query="searchQuery ?? ''" />
            </dd>
          </div>
          <div class="space-y-1">
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">RAM</dt>
            <dd class="text-sm font-medium text-gray-900 dark:text-white">
              <HighlightText :text="String(specsToShow.ram)" :query="searchQuery ?? ''" />
            </dd>
          </div>
          <div class="space-y-1">
            <dt
              class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400"
              data-testid="runner-disk-label"
            >
              {{ provider === 'hetzner' ? 'NVMe SSD' : 'SSD' }}
            </dt>
            <dd class="text-sm font-medium text-gray-900 dark:text-white">
              <HighlightText :text="String(specsToShow.nvmeSsd)" :query="searchQuery ?? ''" />
            </dd>
          </div>
          <div class="space-y-1">
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Concurrency
            </dt>
            <dd class="text-sm font-medium text-gray-900 dark:text-white">
              <HighlightText :text="String(concurrencyToShow)" :query="searchQuery ?? ''" />
            </dd>
          </div>
          <div v-if="!github" class="space-y-1">
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Executor
            </dt>
            <dd class="text-sm font-medium text-gray-900 dark:text-white">
              <HighlightText :text="String(executorToShow)" :query="searchQuery ?? ''" />
            </dd>
          </div>
          <template v-else>
            <div
              v-for="([label, value], index) in githubRows"
              :key="label"
              class="min-w-0 space-y-1"
              data-testid="runner-github-scope"
            >
              <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {{ label }}
              </dt>
              <dd class="text-sm font-medium text-gray-900 dark:text-white">
                <a
                  v-if="index === 0"
                  :href="githubLink"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex max-w-full items-center gap-1 hover:underline underline-offset-2"
                  :title="value"
                >
                  <span class="truncate"
                    ><HighlightText :text="value" :query="searchQuery ?? ''"
                  /></span>
                  <ArrowTopRightOnSquareIcon
                    class="size-3.5 shrink-0 text-gray-400 dark:text-gray-500"
                    aria-hidden="true"
                  />
                </a>
                <HighlightText v-else :text="value" :query="searchQuery ?? ''" />
              </dd>
            </div>
          </template>
          <div class="space-y-1">
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">IPv4</dt>
            <dd class="text-sm font-medium text-gray-900 dark:text-white font-mono">
              <template v-if="ipv4ToShow">
                <button
                  type="button"
                  class="inline-flex items-center gap-2 hover:underline underline-offset-2"
                  :aria-label="ipv4Copied ? 'Copied!' : 'Click to copy'"
                  :title="ipv4Copied ? 'Copied!' : 'Click to copy'"
                  @click="onCopyIp(ipv4ToShow, 'v4')"
                >
                  <span class="font-mono"
                    ><HighlightText :text="ipv4ToShow" :query="searchQuery ?? ''"
                  /></span>
                  <span
                    v-if="ipv4Copied"
                    class="text-[11px] font-normal text-gray-500 dark:text-gray-400"
                  >
                    Copied!
                  </span>
                </button>
              </template>
              <template v-else>—</template>
            </dd>
          </div>
          <div class="space-y-1">
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">IPv6</dt>
            <dd class="text-sm font-medium text-gray-900 dark:text-white font-mono">
              <template v-if="ipv6ToShow">
                <button
                  type="button"
                  class="inline-flex items-center gap-2 hover:underline underline-offset-2"
                  :aria-label="ipv6Copied ? 'Copied!' : 'Click to copy'"
                  :title="ipv6Copied ? 'Copied!' : 'Click to copy'"
                  @click="onCopyIp(ipv6ToShow, 'v6')"
                >
                  <span class="font-mono"
                    ><HighlightText :text="ipv6ToShow" :query="searchQuery ?? ''"
                  /></span>
                  <span
                    v-if="ipv6Copied"
                    class="text-[11px] font-normal text-gray-500 dark:text-gray-400"
                  >
                    Copied!
                  </span>
                </button>
              </template>
              <template v-else>—</template>
            </dd>
          </div>
          <div class="space-y-1">
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Provider
            </dt>
            <dd
              class="text-sm font-medium text-gray-900 dark:text-white"
              data-testid="runner-provider"
            >
              <HighlightText :text="providerTexts.name" :query="searchQuery ?? ''" />
            </dd>
          </div>
          <div class="space-y-1">
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Location
            </dt>
            <dd class="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-1">
              <span v-if="countryInfo.flag" aria-hidden="true">{{ countryInfo.flag }}</span>
              <span><HighlightText :text="locationLabel" :query="searchQuery ?? ''" /></span>
            </dd>
          </div>
          <div class="space-y-1">
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Monthly price
            </dt>
            <dd
              class="text-sm font-medium text-gray-900 dark:text-white"
              :title="monthlyPriceTitle"
            >
              {{ monthlyPriceToShow }}
            </dd>
          </div>
          <div v-if="platform === 'GITLAB'" class="min-w-0 space-y-1" data-testid="runner-cache">
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Cache</dt>
            <dd
              class="truncate text-sm font-medium text-gray-900 dark:text-white"
              :title="cacheName ?? undefined"
            >
              <HighlightText v-if="cacheName" :text="cacheName" :query="searchQuery ?? ''" />
              <template v-else>—</template>
            </dd>
          </div>
          <div class="min-w-0 space-y-1" data-testid="runner-metrics">
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Metrics destination
            </dt>
            <dd class="text-sm font-medium text-gray-900 dark:text-white">
              <HighlightText
                v-if="metricsConnectionName"
                :text="metricsConnectionName"
                :query="searchQuery ?? ''"
              />
              <template v-else>—</template>
            </dd>
            <dd
              v-if="metricsConnectionName && metricsStatus && metricsStatus.status !== 'ok'"
              class="text-xs text-amber-700 dark:text-amber-400"
              :title="metricsStatusLine"
              data-testid="runner-metrics-status"
            >
              {{ metricsStatusLine }}
            </dd>
          </div>
          <div v-if="watchdogInfo" class="min-w-0 space-y-1" data-testid="runner-watchdog">
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Watchdog
            </dt>
            <dd class="text-sm font-medium text-gray-900 dark:text-white">
              {{ watchdogInfo.state }}
            </dd>
            <dd
              v-if="watchdogInfo.restarts"
              class="text-xs"
              :class="
                watchdogInfo.resumeFailed
                  ? 'text-amber-700 dark:text-amber-400'
                  : 'text-gray-500 dark:text-gray-400'
              "
              :title="watchdogInfo.restartsTitle || undefined"
              data-testid="runner-watchdog-restarts"
            >
              {{ watchdogInfo.restarts }}
            </dd>
            <dd
              v-if="watchdogInfo.limitNote"
              class="text-xs text-amber-700 dark:text-amber-400"
              data-testid="runner-watchdog-limit"
            >
              {{ watchdogInfo.limitNote }}
            </dd>
          </div>
          <div v-if="gitlabInstanceToShow" class="min-w-0 space-y-1">
            <dt class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              GitLab instance
            </dt>
            <dd class="text-sm font-medium text-gray-900 dark:text-white">
              <a
                :href="gitlabInstanceToShow"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex max-w-full items-center gap-1 hover:underline underline-offset-2"
                :title="gitlabInstanceToShow"
              >
                <span class="truncate"
                  ><HighlightText :text="gitlabInstanceToShow" :query="searchQuery ?? ''"
                /></span>
                <ArrowTopRightOnSquareIcon
                  class="size-3.5 shrink-0 text-gray-400 dark:text-gray-500"
                  aria-hidden="true"
                />
              </a>
            </dd>
          </div>
        </dl>

        <div
          v-if="monitoring"
          class="mt-4 border-t border-gray-200 pt-4 dark:border-white/10"
          :class="{ 'xl:mt-0 xl:border-t-0 xl:border-l xl:pt-0 xl:pl-6': wide }"
          data-testid="runner-monitoring"
        >
          <RunnerMonitoringPanel
            v-bind="monitoring"
            :concurrency="props.concurrency"
            :watchdog-restarts="watchdog?.restarts ?? null"
          />
        </div>
      </div>
    </div>
    <!-- Fix Token Modal -->
    <TransitionRoot as="template" :show="showFixModal">
      <Dialog as="div" class="relative z-50" @close="closeFixModal">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-500/75 transition-opacity dark:bg-gray-900/80" />
        </TransitionChild>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div
            class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
          >
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 dark:bg-gray-800"
              >
                <div>
                  <div
                    class="mx-auto flex size-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30"
                  >
                    <ExclamationTriangleIcon
                      class="size-6 text-red-600 dark:text-red-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div class="mt-3 text-center sm:mt-5">
                    <DialogTitle
                      as="h3"
                      class="text-base font-semibold leading-6 text-gray-900 dark:text-white"
                    >
                      {{ github ? 'Replace GitHub Token' : 'Fix Runner Token' }}
                    </DialogTitle>
                    <div class="mt-2">
                      <p v-if="github" class="text-sm text-gray-500 dark:text-gray-400">
                        The runner could not register with GitHub. Its server is recreated so that
                        it registers again. Enter a new token to replace the stored one first, or
                        leave the field empty to keep it.
                      </p>
                      <p v-else class="text-sm text-gray-500 dark:text-gray-400">
                        The current gitlab token is invalid. Please provide a new GitLab Runner
                        Token to re-register this runner. You can also update the GitLab host if it
                        was set incorrectly.
                      </p>
                    </div>
                  </div>
                </div>
                <form
                  v-if="github"
                  @submit.prevent="onGithubFixSubmit"
                  class="mt-5 sm:mt-6 space-y-4"
                >
                  <div class="text-left">
                    <label
                      for="fix-github-token"
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >{{ GITHUB_TOKEN_LABEL }}
                      <span class="font-normal text-gray-500 dark:text-gray-400"
                        >(optional)</span
                      ></label
                    >
                    <div class="mt-1">
                      <input
                        id="fix-github-token"
                        v-model="newGithubToken"
                        type="password"
                        name="fix-github-token"
                        autocomplete="off"
                        aria-describedby="fix-github-token-desc"
                        class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:border-white/10 dark:bg-gray-800 dark:text-white"
                        placeholder="github_pat_…"
                        data-1p-ignore
                      />
                    </div>
                    <p
                      v-if="fixGithubTokenError"
                      class="mt-2 text-sm text-red-600 dark:text-red-400"
                    >
                      {{ fixGithubTokenError }}
                    </p>
                    <p
                      id="fix-github-token-desc"
                      class="mt-2 text-sm text-gray-500 dark:text-gray-400"
                    >
                      {{ githubTokenHint(github) }}
                      <a
                        :href="GITHUB_TOKEN_PAGE"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                        >Create a fine-grained token on GitHub</a
                      >.
                    </p>
                  </div>
                  <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      class="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 sm:w-auto dark:border-white/10 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                      @click="closeFixModal"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      class="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:w-auto"
                    >
                      {{ newGithubToken.trim() ? 'Save and recreate' : 'Recreate' }}
                    </button>
                  </div>
                </form>
                <form v-else @submit.prevent="onFixSubmit" class="mt-5 sm:mt-6 space-y-4">
                  <div class="text-left">
                    <label
                      for="fix-gitlab-host"
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >GitLab Host</label
                    >
                    <div class="mt-1">
                      <input
                        v-model.trim="newGitlabHost"
                        type="url"
                        name="fix-gitlab-host"
                        id="fix-gitlab-host"
                        required
                        autocomplete="url"
                        class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:border-white/10 dark:bg-gray-800 dark:text-white"
                        placeholder="https://gitlab.example.com"
                      />
                    </div>
                    <p
                      v-if="fixHostError"
                      class="mt-2 text-sm text-red-600 dark:text-red-400"
                    >
                      {{ fixHostError }}
                    </p>
                    <p v-else class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Base URL of your GitLab instance.
                    </p>
                  </div>
                  <div class="text-left">
                    <label
                      for="token"
                      class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >New GitLab Runner Token</label
                    >
                    <div class="mt-1">
                      <input
                        v-model="newToken"
                        type="password"
                        name="token"
                        id="token"
                        required
                        class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:border-white/10 dark:bg-gray-800 dark:text-white"
                        placeholder="glrt-..."
                      />
                    </div>
                  </div>
                  <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      class="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 sm:w-auto dark:border-white/10 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                      @click="showFixModal = false"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      class="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:w-auto"
                    >
                      Save
                    </button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
    <TransitionRoot as="template" :show="showPauseModal">
      <Dialog as="div" class="relative z-50" @close="showPauseModal = false">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-500/75 transition-opacity dark:bg-gray-900/80" />
        </TransitionChild>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div
            class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
          >
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 dark:bg-gray-800"
                data-testid="pause-dialog"
              >
                <DialogTitle
                  as="h3"
                  class="text-base font-semibold leading-6 text-gray-900 dark:text-white"
                >
                  {{ isDraining ? 'Pause now?' : 'Pause runner' }}
                </DialogTitle>
                <div class="mt-2 space-y-2 text-sm text-gray-500 dark:text-gray-400">
                  <template v-if="isDraining">
                    <p>
                      This runner takes no new jobs and is paused once its running jobs have
                      finished, at the latest one hour after the pause was requested.
                    </p>
                    <p>Pausing now deletes its server at once and cancels the running jobs.</p>
                    <p v-if="platform === 'GITHUB'" data-testid="pause-default-labels-note">
                      {{ GITHUB_DEFAULT_LABELS_NOTE }}
                    </p>
                  </template>
                  <template v-else>
                    <p>
                      <span class="font-medium text-gray-900 dark:text-white">After running jobs:</span>
                      the runner takes no new jobs, and its server is deleted once the running jobs
                      have finished, after at most one hour.
                    </p>
                    <p>
                      <span class="font-medium text-gray-900 dark:text-white">Now:</span>
                      the server is deleted at once, which cancels running jobs.
                    </p>
                    <p v-if="platform === 'GITHUB'" data-testid="pause-default-labels-note">
                      {{ GITHUB_DEFAULT_LABELS_NOTE }}
                    </p>
                  </template>
                </div>
                <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                  <button
                    v-if="!isDraining"
                    type="button"
                    class="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:w-auto"
                    @click="confirmPause(false)"
                  >
                    Pause after running jobs
                  </button>
                  <button
                    type="button"
                    class="mt-3 inline-flex w-full justify-center rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-700 shadow-sm hover:bg-red-50 sm:mt-0 sm:w-auto dark:border-red-900/50 dark:bg-gray-700 dark:text-red-400 dark:hover:bg-gray-600"
                    @click="confirmPause(true)"
                  >
                    Pause now
                  </button>
                  <button
                    type="button"
                    class="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 sm:mt-0 sm:w-auto dark:border-white/10 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                    @click="showPauseModal = false"
                  >
                    {{ isDraining ? 'Keep waiting' : 'Cancel' }}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
    <!-- Delete Unknown Runner Confirmation Modal -->
    <TransitionRoot as="template" :show="showDeleteUnknownModal">
      <Dialog as="div" class="relative z-50" @close="showDeleteUnknownModal = false">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-500/75 transition-opacity dark:bg-gray-900/80" />
        </TransitionChild>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div
            class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
          >
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 dark:bg-gray-800"
              >
                <div class="sm:flex sm:items-start">
                  <div
                    class="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10 dark:bg-red-900/30"
                  >
                    <ExclamationTriangleIcon
                      class="size-6 text-red-600 dark:text-red-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <DialogTitle
                      as="h3"
                      class="text-base font-semibold leading-6 text-gray-900 dark:text-white"
                    >
                      Delete Unknown Runner
                    </DialogTitle>
                    <div class="mt-2">
                      <p class="text-sm text-gray-500 dark:text-gray-400">
                        {{
                          provider === 'hetzner'
                            ? 'If you delete an unknown runner, the ip and hetzner server will not be deleted.'
                            : `If you delete an unknown runner, its reserved IPs and ${providerTexts.name} droplet will not be deleted.`
                        }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                  <button
                    type="button"
                    class="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:w-auto"
                    @click="confirmDeleteUnknown"
                  >
                    Confirm
                  </button>
                  <button
                    type="button"
                    class="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 sm:mt-0 sm:w-auto dark:border-white/10 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                    @click="showDeleteUnknownModal = false"
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {
  ArrowTopRightOnSquareIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  PauseIcon,
  PencilSquareIcon,
  PlayIcon,
  TrashIcon,
} from '@heroicons/vue/20/solid';
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue';
import {getCountryInfo} from '@/lib/country';
import {normalizeGitlabInstance} from '@/lib/gitlab-instance';
import HighlightText from '@/components/HighlightText.vue';
import RunnerMonitoringPanel from '@/components/RunnerMonitoringPanel.vue';
import type { RunnerMonitoringView } from '@/lib/runner-metrics';
import type { MetricsStatus } from '@/api/runners';
import { exportStatusText, reportedAgo } from '@/lib/monitoring-format';
import {formatEnum, formatMonthlyNetPrice, monthlyNetPriceTitle, type MonthlyPriceLike} from '@/lib/format';
import type { GithubRunnerScope, RunnerPlatform, RunnerWatchdog } from '@/api/runners';
import {
  GITHUB_TOKEN_PAGE,
  githubScopeUrl,
  PLATFORM_LABELS,
  validateGithubToken,
} from '@/lib/platforms';
import { type CloudProvider, PROVIDER_TEXTS } from '@/lib/providers';
import { GITHUB_TOKEN_LABEL, githubScopeRows, githubTokenHint } from '@/lib/mcp-handoffs';
import { useNow } from '@/lib/now';
import { burstRunningTime, type BurstTimes } from '@/lib/scaling';
import { watchdogSummary } from '@/lib/watchdog';

// Taller card with content: Name, Specifications (chip, vCPU, RAM, NVMe SSD), Monthly price
interface Specs {
  chip?: string;
  vcpu?: string | number;
  ram?: string;
  nvmeSsd?: string | number;
}

interface Props {
  name?: string;
  specs?: Specs;
  // Controls whether the runner is currently paused (affects button label/action)
  isPaused?: boolean;
  // Show or hide the Delete button
  isPauseButtonDisabled?: boolean;
  // Separate in-flight/transitional disable for Duplicate (independent of Pause/Resume)
  isDuplicateButtonDisabled?: boolean;
  isDeleteButtonDisabled?: boolean;
  showDelete?: boolean;
  // Optional size control; defaults to a taller card.
  size?: 'sm' | 'md' | 'lg';
  // Runner status label for the status button (presentation only)
  status?:
    | 'active'
    | 'creating'
    | 'paused'
    | 'configuring'
    | 'error'
    | 'pausing'
    | 'unknown'
    | 'invalid_gitlab_token'
    | 'registration_failed';
  // IP addresses to display next to the status
  ipv4?: string;
  ipv6?: string;
  // ISO country code for the runner location/country display
  countryCode?: string;
  // Optional city for the runner location display
  city?: string;
  // Runner concurrency and executor
  concurrency?: number | null;
  executor?: string | null;
  // Error message to display
  errorMessage?: string;
  // Current GitLab host of this runner (used to prefill the Fix dialog)
  gitlabHost?: string;
  // Whether to show the connected GitLab instance (only when the user's
  // runners span more than one distinct instance)
  showGitlabInstance?: boolean;
  // Search query for highlighting matched text
  searchQuery?: string;
  // Hetzner Cloud net monthly list price for this runner's product + location
  monthlyPrice?: MonthlyPriceLike | null;
  /** Name of the cache the runner uses, if any. */
  cacheName?: string | null;
  /**
   * False when Hetzner reports this server type as unavailable in the runner location.
   * Disables Duplicate/Resume and warns before Pause.
   */
  duplicatable?: boolean;
  /** Dashboard metrics; null for runners that are not active or do not show them in the dashboard. */
  monitoring?: RunnerMonitoringView | null;
  wide?: boolean;
  /** Name of the Metrics connection the runner uses, if any. */
  metricsConnectionName?: string | null;
  /** The collector's last reported Metrics result for the current VM. */
  metricsStatus?: { status: MetricsStatus; reportedAt: string } | null;
  /** Present once the runner's watchdog was ever enabled. */
  watchdog?: RunnerWatchdog | null;
  readOnly?: boolean;
  planRequired?: boolean;
  platform?: RunnerPlatform;
  /** Where a GitHub runner is registered; shown instead of the executor. */
  github?: GithubRunnerScope | null;
  /** Shows the platform of GitLab runners too, when the organization has runners of both platforms. */
  showPlatform?: boolean;
  /** Hetzner for runners whose response has no provider. */
  provider?: CloudProvider;
  /**
   * The anchor's name for a burst runner, which only its scaling policy changes, so it can only be deleted; an empty
   * string when the anchor is gone. Null for other runners.
   */
  burstOf?: string | null;
  /** When a burst runner was reserved and its server created; null for other runners. */
  burstTimes?: BurstTimes | null;
}

const PAUSE_UNAVAILABLE_HINT =
  'If paused, this runner cannot be resumed until the server type becomes available again.';
const PLAN_REQUIRED_HINT = 'This needs an active plan.';
const BURST_HINT =
  'Created by a scaling policy, which also removes it. It cannot be edited, paused or duplicated; deleting it is possible.';
const GITHUB_DEFAULT_LABELS_NOTE =
  'While a job is running, GitHub can still give this runner jobs that ask only for the default labels ' +
  '(self-hosted, operating system, architecture) until that job has finished.';

const props = withDefaults(defineProps<Props>(), {
  size: 'lg',
  isPaused: false,
  showDelete: true,
  status: 'active',
  duplicatable: true,
  isDuplicateButtonDisabled: false,
  readOnly: false,
  planRequired: false,
  platform: 'GITLAB',
  github: null,
  showPlatform: false,
  provider: 'hetzner',
  wide: false,
  burstOf: null,
  burstTimes: null,
  watchdog: null,
});

const emit = defineEmits<{
  (e: 'resume'): void;
  /** `force` deletes the server at once instead of letting running jobs finish. */
  (e: 'pause', options: { force: boolean }): void;
  (e: 'delete'): void;
  (e: 'force-delete'): void;
  (e: 'status-click'): void;
  (e: 'edit'): void;
  (e: 'duplicate'): void;
  (e: 'fix', payload: { runnerToken: string; gitlabHost: string }): void;
  /** An empty token recreates the server with the stored token. */
  (e: 'fix-github', token: string): void;
  (e: 'dismiss-error'): void;
}>();

const nameToShow = computed(() => props.name || '—');
const now = useNow();
const burstRunningText = computed(() =>
  props.burstTimes ? burstRunningTime(props.burstTimes, now.value) : null
);
const platformShown = computed(() => props.showPlatform || props.platform !== 'GITLAB');
const providerTexts = computed(() => PROVIDER_TEXTS[props.provider]);
const githubRows = computed(() => (props.github ? githubScopeRows(props.github) : []));
const githubLink = computed(() => (props.github ? githubScopeUrl(props.github) : ''));
const needsFix = computed(
  () =>
    (props.status === 'invalid_gitlab_token' && !props.github) ||
    (props.status === 'registration_failed' && !!props.github)
);
const metricsStatusLine = computed(() => {
  const report = props.metricsStatus;
  if (!report) return '';
  const ago = reportedAgo(report.reportedAt, Date.now());
  return ago ? `${exportStatusText(report.status)} · ${ago}` : exportStatusText(report.status);
});
const watchdogInfo = computed(() =>
  props.watchdog ? watchdogSummary(props.watchdog, now.value) : null
);
const monthlyPriceFormatted = computed(() => formatMonthlyNetPrice(props.monthlyPrice));
const monthlyPriceToShow = computed(() => monthlyPriceFormatted.value || '—');
const monthlyPriceTitle = computed(() =>
  monthlyNetPriceTitle(monthlyPriceFormatted.value, providerTexts.value.priceSource)
);
const specsToShow = computed(() => ({
  chip: props.specs?.chip || '—',
  vcpu: props.specs?.vcpu ?? '—',
  ram: props.specs?.ram || '—',
  nvmeSsd: props.specs?.nvmeSsd ?? '—',
}));

// Normalize IP displays; hide if empty/undefined
const ipv4ToShow = computed(() => (props.ipv4 && props.ipv4.trim().length > 0 ? props.ipv4 : ''));
const ipv6ToShow = computed(() => (props.ipv6 && props.ipv6.trim().length > 0 ? props.ipv6 : ''));

// Concurrency and executor displays
const concurrencyToShow = computed(() =>
  typeof props.concurrency === 'number' && !Number.isNaN(props.concurrency)
    ? props.concurrency
    : '—'
);
const executorToShow = computed(() =>
  props.executor && props.executor.trim().length > 0 ? props.executor : '—'
);

// Normalized GitLab instance for display and linking; empty when the host is
// missing/malformed or when all runners share one instance (field hidden)
const gitlabInstanceToShow = computed(() =>
  props.showGitlabInstance ? (normalizeGitlabInstance(props.gitlabHost) ?? '') : ''
);

// Refs for DOM and local state
const deleteBtn = ref<HTMLElement | null>(null);

// Local paused state mirrors prop and updates immediately on click for snappy UX
const isPausedLocal = ref<boolean>(props.isPaused);
const isPauseButtonDisabled = ref<boolean>(props.isPauseButtonDisabled);
const isDuplicateButtonDisabled = ref<boolean>(props.isDuplicateButtonDisabled);
const isDeleteButtonDisabled = ref<boolean>(props.isDeleteButtonDisabled);
const showFixModal = ref(false);
const showDeleteUnknownModal = ref(false);
const showPauseModal = ref(false);
const newToken = ref('');
const newGitlabHost = ref('');
const fixHostError = ref('');
const newGithubToken = ref('');
const fixGithubTokenError = ref('');

const isUnavailable = computed(() => props.duplicatable === false);
const showPauseCapacityWarning = computed(() => isUnavailable.value && !isPausedLocal.value);
const isDuplicateDisabled = computed(
  () => props.planRequired || isUnavailable.value || isDuplicateButtonDisabled.value
);
const isPauseResumeDisabled = computed(
  () =>
    isPauseButtonDisabled.value ||
    (isPausedLocal.value && (props.planRequired || isUnavailable.value))
);
const resumeHint = computed(() =>
  props.planRequired
    ? PLAN_REQUIRED_HINT
    : isUnavailable.value
      ? `Cannot resume: ${providerTexts.value.unavailable}`
      : null
);

const duplicateHint = computed(() =>
  props.planRequired
    ? PLAN_REQUIRED_HINT
    : isUnavailable.value
      ? `Cannot duplicate: ${providerTexts.value.unavailable}`
      : null
);
const duplicateButtonTitle = computed(() => duplicateHint.value ?? 'Duplicate');
const duplicateAriaLabel = computed(() =>
  duplicateHint.value ? `Duplicate runner. ${duplicateHint.value}` : 'Duplicate runner'
);

const isDraining = computed(() => props.status === 'pausing' && !isPausedLocal.value);
const pauseResumeLabel = computed(() =>
  isPausedLocal.value ? 'Resume' : isDraining.value ? 'Pause now' : 'Pause'
);
const pauseResumeButtonTitle = computed(() => {
  if (isPausedLocal.value) {
    return resumeHint.value ?? 'Resume';
  }
  if (isDraining.value) return 'Pause now, cancelling the running jobs';
  return isUnavailable.value ? PAUSE_UNAVAILABLE_HINT : 'Pause';
});
const pauseResumeAriaLabel = computed(() => {
  if (isPausedLocal.value) {
    return resumeHint.value ? `Resume runner. ${resumeHint.value}` : 'Resume runner';
  }
  if (isDraining.value) return 'Pause runner now';
  return isUnavailable.value
    ? `Pause runner. ${PAUSE_UNAVAILABLE_HINT}`
    : 'Pause runner';
});
const pauseResumeButtonClass = computed(() => {
  if (showPauseCapacityWarning.value) {
    return 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 focus-visible:outline-amber-600 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300 dark:hover:bg-amber-500/20';
  }
  return 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus-visible:outline-indigo-600 dark:border-white/10 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700';
});

function openFixModal() {
  newGithubToken.value = '';
  fixGithubTokenError.value = '';
  newToken.value = '';
  newGitlabHost.value = props.gitlabHost || 'https://gitlab.com/';
  fixHostError.value = '';
  showFixModal.value = true;
}

function onFixSubmit() {
  const token = newToken.value.trim();
  const host = newGitlabHost.value.trim();
  if (!token) return;
  if (!host) {
    fixHostError.value = 'GitLab host is required';
    return;
  }
  if (!/^https?:\/\//.test(host)) {
    fixHostError.value = 'Enter a valid URL (starting with http or https)';
    return;
  }
  fixHostError.value = '';
  emit('fix', { runnerToken: token, gitlabHost: host });
  showFixModal.value = false;
  newToken.value = '';
  newGitlabHost.value = '';
}

function onGithubFixSubmit() {
  const token = newGithubToken.value.trim();
  fixGithubTokenError.value = validateGithubToken(token);
  if (fixGithubTokenError.value) return;
  emit('fix-github', token);
  closeFixModal();
}

function closeFixModal() {
  showFixModal.value = false;
  newGithubToken.value = '';
}

watch(
  [
    () => props.isPaused,
    () => props.isPauseButtonDisabled,
    () => props.isDuplicateButtonDisabled,
    () => props.isDeleteButtonDisabled,
  ],
  ([paused, pauseDisabled, duplicateDisabled, deleteDisabled]) => {
    isPausedLocal.value = paused;
    isPauseButtonDisabled.value = pauseDisabled;
    isDuplicateButtonDisabled.value = duplicateDisabled;
    isDeleteButtonDisabled.value = deleteDisabled;
  }
);

// Status button helpers
const statusLabel = computed(() => {
  return formatEnum(props.status || 'active');
});
const isPulsing = computed(() => {
  switch (props.status) {
    case 'creating':
    case 'configuring':
    case 'active':
      return true;
    default:
      return false;
  }
});
const statusDotClass = computed(() => {
  switch (props.status) {
    case 'active':
      return 'bg-green-500';
    case 'creating':
      return 'bg-amber-400';
    case 'paused':
      return 'bg-gray-500';
    case 'configuring':
      return 'bg-amber-400';
    case 'error':
    case 'invalid_gitlab_token':
    case 'registration_failed':
      return 'bg-red-500';
    case 'pausing':
      return 'bg-amber-300';
    case 'unknown':
      return 'bg-gray-400';
    default:
      return 'bg-gray-500';
  }
});
const statusBadgeClass = computed(() => {
  switch (props.status) {
    case 'active':
      return 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20 dark:bg-green-500/10 dark:text-green-400 dark:ring-green-500/20';
    case 'creating':
      return 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20';
    case 'configuring':
      return 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20';
    case 'paused':
      return 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20';
    case 'pausing':
      return 'bg-amber-50 text-amber-600 ring-1 ring-inset ring-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20';
    case 'error':
    case 'invalid_gitlab_token':
    case 'registration_failed':
      return 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/10 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/20';
    case 'unknown':
      return 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20';
    default:
      return 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20';
  }
});

const countryInfo = computed(() => getCountryInfo(props.countryCode));
const locationLabel = computed(() => {
  const countryName = countryInfo.value.name || '—';
  const city = props.city?.trim();
  return city ? `${countryName} (${city})` : countryName;
});

function onDuplicate() {
  if (isDuplicateDisabled.value) return;
  emit('duplicate');
}

function onTogglePause() {
  if (isPauseResumeDisabled.value) return;
  if (isPausedLocal.value) {
    // Optimistically toggle local-state for immediate visual feedback
    isPausedLocal.value = false;
    emit('resume');
  } else {
    showPauseModal.value = true;
  }
}

function confirmPause(force: boolean) {
  showPauseModal.value = false;
  emit('pause', { force });
}

// --- IP copy-to-clipboard state and logic ---
const ipv4Copied = ref(false);
const ipv6Copied = ref(false);

const copyTimers: Record<'v4' | 'v6', number | null> = { v4: null, v6: null };

async function writeClipboard(text: string) {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to fallback
  }
  // Fallback for rare environments
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

async function onCopyIp(value: string, kind: 'v4' | 'v6') {
  if (!value) return;
  const ok = await writeClipboard(value);
  if (!ok) return;

  // show copied for 1s
  if (kind === 'v4') {
    ipv4Copied.value = true;
    if (copyTimers.v4) window.clearTimeout(copyTimers.v4);
    copyTimers.v4 = window.setTimeout(() => {
      ipv4Copied.value = false;
      copyTimers.v4 = null;
    }, 1000);
  } else {
    ipv6Copied.value = true;
    if (copyTimers.v6) window.clearTimeout(copyTimers.v6);
    copyTimers.v6 = window.setTimeout(() => {
      ipv6Copied.value = false;
      copyTimers.v6 = null;
    }, 1000);
  }
}

// Delete two-click confirm with animation
const deleteArmed = ref(false);
function onDeleteClick() {
  if (props.status === 'unknown') {
    showDeleteUnknownModal.value = true;
    return;
  }
  if (!deleteArmed.value) {
    deleteArmed.value = true;
  } else {
    // the second click confirms
    emit('delete');
    deleteArmed.value = false;
  }
}

function confirmDeleteUnknown() {
  emit('force-delete');
  showDeleteUnknownModal.value = false;
}

function disarmDelete() {
  if (deleteArmed.value) deleteArmed.value = false;
}

function onDocumentClick(e: MouseEvent) {
  const target = e.target as Node | null;
  if (deleteArmed.value) {
    // If click is outside the delete button, disarm
    if (deleteBtn.value && target && !deleteBtn.value.contains(target)) {
      disarmDelete();
    }
  }
}

function onDocumentKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' || e.key === 'Esc') {
    if (deleteArmed.value) {
      e.stopPropagation();
      disarmDelete();
    }
  }
}

onMounted(() => {
  window.addEventListener('click', onDocumentClick, true);
  window.addEventListener('keydown', onDocumentKeydown, true);
});

onBeforeUnmount(() => {
  window.removeEventListener('click', onDocumentClick, true);
  window.removeEventListener('keydown', onDocumentKeydown, true);
  if (copyTimers.v4) window.clearTimeout(copyTimers.v4);
  if (copyTimers.v6) window.clearTimeout(copyTimers.v6);
});

const deleteButtonClass = computed(() => {
  // Smoother animation with no layout shift while allowing text label sizing.
  const base =
    'inline-flex items-center gap-2 rounded-md text-sm font-medium px-3 py-1.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 will-change-transform transition-colors transition-transform duration-300 ease-in-out motion-reduce:transition-none';
  const normal = `${base} disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 hover:shadow active:scale-95 focus-visible:outline-indigo-600 dark:border-white/10 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700`;
  const armed = `${base} disabled:opacity-50 disabled:cursor-not-allowed border border-transparent bg-red-600 text-white hover:bg-red-500 ring-2 ring-red-300 shadow-md scale-105 hover:scale-[1.07] focus-visible:outline-red-600`;
  return deleteArmed.value ? armed : normal;
});

const cardClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'min-h-40';
    case 'md':
      return 'min-h-48';
    case 'lg':
    default:
      return 'min-h-56';
  }
});
</script>

<style scoped>
/* Width is 100% of grid cell; min-height ensures a taller rectangle */
</style>
